import React, { useMemo } from 'react';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/lib/echarts';
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/pie';
import 'echarts/lib/component/grid';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/tooltip';
import { Badge, Card, CardBody } from 'reactstrap';

const palette = ['#2c7be5', '#00d27a', '#f5803e', '#e63757', '#27bcfd', '#6b5eae', '#00a693', '#748194'];

const buildCourseOption = rows => ({
  animationDuration: 950,
  animationEasing: 'cubicOut',
  color: ['#2c7be5', '#f5803e'],
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' }
  },
  legend: {
    top: 0,
    right: 0
  },
  grid: {
    top: 38,
    left: 12,
    right: 12,
    bottom: 16,
    containLabel: true
  },
  xAxis: {
    type: 'category',
    data: rows.map(row => row.name),
    axisTick: { show: false },
    axisLine: { lineStyle: { color: '#d8e2ef' } },
    axisLabel: {
      color: '#5e6e82',
      interval: 0
    }
  },
  yAxis: {
    type: 'value',
    minInterval: 1,
    splitLine: { lineStyle: { color: '#edf2f9' } },
    axisLabel: { color: '#748194' }
  },
  series: [
    {
      name: 'Natijalar',
      type: 'bar',
      barMaxWidth: 34,
      data: rows.map(row => row.result_count || 0),
      itemStyle: { borderRadius: [10, 10, 0, 0] }
    },
    {
      name: 'Signal',
      type: 'bar',
      barMaxWidth: 22,
      data: rows.map(row => row.signal_count || 0),
      itemStyle: { borderRadius: [10, 10, 0, 0] }
    }
  ]
});

const buildNationalityOption = rows => ({
  animationDuration: 1100,
  animationEasing: 'quarticOut',
  color: palette,
  tooltip: {
    trigger: 'item',
    formatter: params => `${params.name}<br/>${params.value} ta natija`
  },
  legend: {
    bottom: 0,
    type: 'scroll',
    icon: 'circle',
    itemWidth: 9,
    itemHeight: 9,
    textStyle: { color: '#5e6e82' }
  },
  series: [
    {
      name: 'Millati',
      type: 'pie',
      radius: ['43%', '72%'],
      center: ['50%', '42%'],
      itemStyle: {
        borderColor: '#ffffff',
        borderWidth: 3,
        borderRadius: 7
      },
      label: {
        formatter: '{b}\n{c}',
        color: '#1f2937',
        fontWeight: 700
      },
      data: rows.map(row => ({
        name: row.name,
        value: row.result_count || 0
      }))
    }
  ]
});

const SegmentCard = ({ title, total, children }) => (
  <Card className="border-0 shadow-sm med-dashboard-card psi-analysis-segment-card">
    <CardBody>
      <div className="psi-analysis-segment-card__head">
        <h5 className="mb-1">{title}</h5>
        <Badge pill className="psi-analysis-segment-card__badge">
          {total} ta
        </Badge>
      </div>
      {children}
    </CardBody>
  </Card>
);

const AnalysisSegmentCharts = ({ segments }) => {
  const courseRows = useMemo(() => (segments?.courses || []).slice(0, 8), [segments]);
  const nationalityRows = useMemo(() => (segments?.nationalities || []).slice(0, 8), [segments]);
  const courseOption = useMemo(() => buildCourseOption(courseRows), [courseRows]);
  const nationalityOption = useMemo(() => buildNationalityOption(nationalityRows), [nationalityRows]);
  const totalCourseResults = courseRows.reduce((sum, row) => sum + Number(row.result_count || 0), 0);
  const totalNationalityResults = nationalityRows.reduce((sum, row) => sum + Number(row.result_count || 0), 0);

  if (!courseRows.length && !nationalityRows.length) {
    return null;
  }

  return (
    <div className="psi-analysis-segments mb-3">
      <SegmentCard title="Kurs kesimida tahlil" total={totalCourseResults}>
        {courseRows.length ? (
          <ReactEchartsCore echarts={echarts} option={courseOption} style={{ height: 260 }} notMerge lazyUpdate />
        ) : (
          <div className="psi-results-empty">Kurs kesimida natija topilmadi.</div>
        )}
      </SegmentCard>
      <SegmentCard title="Millati kesimida tahlil" total={totalNationalityResults}>
        {nationalityRows.length ? (
          <ReactEchartsCore echarts={echarts} option={nationalityOption} style={{ height: 260 }} notMerge lazyUpdate />
        ) : (
          <div className="psi-results-empty">Millati kesimida natija topilmadi.</div>
        )}
      </SegmentCard>
    </div>
  );
};

export default AnalysisSegmentCharts;
